// ReportMetricLineTrend.jsx

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Container, Table, TableBody, Typography } from "@mui/material";
import EChartsReact from "echarts-for-react";

import {
    COMPARISON_FILTER,
    DISPLAY_ELEMENT_HELPER,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_WEEK,
    GROUP_DATE_YEAR,
} from "../../../helpers/filters";
import { getWeekYear } from "../../../helpers/dates";
import { fns } from "../../../helpers/common";
import HoverPaper from "../HoverPaper";
import NoDataRow from "../../NoDataRow";
import { METRIC_COLORS } from "../../../helpers/colours";
import InfoIcon from "../../InfoIcon";

const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

const getMetricValue = (row, metric, suffix = "") => {
    if (metric === "ADRTotal") {
        return row[`roomNightsTotal${suffix}`]
            ? row[`revenueRoom${suffix}`] / row[`roomNightsTotal${suffix}`]
            : 0;
    }

    if (metric === "ALoSTotal") {
        return row[`bookingsRooms${suffix}`]
            ? row[`roomNightsTotal${suffix}`] / row[`bookingsRooms${suffix}`]
            : 0;
    }

    if (metric === "ABWTotal") {
        return row[`bookingsRooms${suffix}`]
            ? row[`BWTotal${suffix}`] / row[`bookingsRooms${suffix}`]
            : 0;
    }

    if (metric === "cancellationRate") {
        return row[`bookingsTotal${suffix}`]
            ? (row[`bookingsCancelled${suffix}`] /
                  row[`bookingsTotal${suffix}`]) *
                  100
            : 0;
    }

    return row[`${metric}${suffix}`] ?? 0;
};

export const REPORT_METRIC_LINE_TREND = "REPORT_METRIC_LINE_TREND";
const ReportMetricLineTrend = ({
    data,
    title,
    metric = "revenueTotal",
    aggregation = "Day",
    showLastYear = false,
    comparisonText = "last year",
    description,
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const formatValue = (value) =>
        fns(hotelID, value, DISPLAY_ELEMENT_HELPER[metric]?.format ?? {});

    const chartData = useMemo(() => {
        const grouped = {};
        const fields = [
            ...new Set([
                metric,
                "revenueRoom",
                "roomNightsTotal",
                "bookingsRooms",
                "bookingsTotal",
                "bookingsCancelled",
                "BWTotal",
            ]),
        ];
        Object.values(data ?? {}).forEach(
            ({ keys, result, resultOneYearBefore }) => {
                const k = Object.fromEntries(keys ?? []);

                const year = Number(
                    k[GROUP_DATE_YEAR] ?? new Date().getUTCFullYear(),
                );
                const month = Number(k[GROUP_DATE_MONTH] ?? 1);
                const day = Number(k[GROUP_DATE_DAY] ?? 1);
                const date = new Date(Date.UTC(year, month - 1, day));
                const weekInfo = getWeekYear(date);
                const week = Number(k[GROUP_DATE_WEEK] ?? weekInfo.week);

                const key =
                    aggregation === "Day"
                        ? `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
                        : aggregation === "Week"
                          ? `${weekInfo.year} - Week #${week}`
                          : aggregation === "Year"
                            ? `${year}`
                            : `${year}-${String(month).padStart(2, "0")}`;

                const label =
                    aggregation === "Day"
                        ? `${day} ${MONTHS[month - 1]} ${year}`
                        : aggregation === "Week"
                          ? `Week #${week} - ${weekInfo.year}`
                          : aggregation === "Year"
                            ? `${year}`
                            : `${MONTHS[month - 1]} ${year}`;

                const sort =
                    aggregation === "Day"
                        ? year * 10000 + month * 100 + day
                        : aggregation === "Week"
                          ? weekInfo.year * 100 + week
                          : aggregation === "Year"
                            ? year
                            : year * 100 + month;

                grouped[key] = grouped[key] ?? { label, sort };

                fields.forEach((field) => {
                    grouped[key][field] =
                        (grouped[key][field] ?? 0) +
                        (Number(result?.[field]) || 0);

                    grouped[key][`${field}LastYear`] =
                        (grouped[key][`${field}LastYear`] ?? 0) +
                        (Number(resultOneYearBefore?.[field]) || 0);
                });
            },
        );

        const rows = Object.values(grouped).sort((a, b) => a.sort - b.sort);

        return {
            labels: rows.map((row) => row.label),
            values: rows.map((row) => getMetricValue(row, metric)),
            valuesLastYear: rows.map((row) =>
                getMetricValue(row, metric, "LastYear"),
            ),
        };
    }, [data, metric, aggregation]);

    const metricName = DISPLAY_ELEMENT_HELPER[metric]?.short ?? metric;

    return (
        <>
            <InfoIcon title={title} description={description} />

            <Container maxWidth={false} component={HoverPaper} sx={{ p: 2 }}>
                {chartData.labels.length > 0 ? (
                    <EChartsReact
                        style={{ width: "100%", height: 320 }}
                        option={{
                            tooltip: {
                                trigger: "axis",
                                formatter: (params) => {
                                    const points = Array.isArray(params)
                                        ? params
                                        : [params];

                                    return [
                                        points[0]?.axisValue ?? "",
                                        ...points.map(
                                            ({ marker, seriesName, value }) =>
                                                `${marker}${seriesName}: ${formatValue(value)}`,
                                        ),
                                    ].join("<br/>");
                                },
                            },
                            grid: {
                                top: 40,
                                left: "5%",
                                right: "5%",
                                bottom: 0,
                                containLabel: true,
                            },
                            xAxis: {
                                type: "category",
                                data: chartData.labels,
                            },
                            yAxis: {},
                            series: [
                                {
                                    type: "line",
                                    name: metricName,
                                    data: chartData.values,
                                    smooth: true,
                                    itemStyle: { color: METRIC_COLORS[0] },
                                },
                                ...(showLastYear
                                    ? [
                                          {
                                              type: "line",
                                              name: `${metricName} ${COMPARISON_FILTER[comparisonText]?.label ?? comparisonText}`,
                                              data: chartData.valuesLastYear,
                                              smooth: true,
                                              itemStyle: {
                                                  color: `${METRIC_COLORS[0]}59`,
                                              },
                                              lineStyle: {
                                                  type: "dashed",
                                              },
                                          },
                                      ]
                                    : []),
                            ],
                        }}
                    />
                ) : (
                    <Table>
                        <TableBody>
                            <NoDataRow text="No data for these filters" />
                        </TableBody>
                    </Table>
                )}
            </Container>
        </>
    );
};

export default ReportMetricLineTrend;
